import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { villes } from "@/data/villes";

const BASQUE_IMG = "https://images.unsplash.com/photo-1692623427695-9b265a597014?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxiYXNxdWUlMjBjb3VudHJ5JTIwbGFuZHNjYXBlJTIwZnJhbmNlfGVufDB8fHx8MTc4NzUwOTY2MXww&ixlib=rb-4.1.0&q=85";

const chapters = [
  { n: "01", title: "Une solide expérience", text: "Administration, relation client, ressources humaines et gestion d'entreprise : une expertise acquise au contact quotidien des professionnels." },
  { n: "02", title: "Un accompagnement personnalisé", text: "Chaque situation est unique. Mission ponctuelle ou accompagnement régulier, les prestations s'adaptent à vos besoins réels." },
  { n: "03", title: "Le réseau AADPROX", text: "Membre du réseau national spécialisé dans l'accompagnement administratif : outils, méthodes, formations et veille juridique." },
  { n: "04", title: "Une sérénité garantie", text: "Une activité couverte par une assurance Responsabilité Civile Professionnelle, pour une collaboration en toute confiance." },
  { n: "05", title: "Sur site ou à distance", text: "Intervention dans vos locaux, à votre domicile ou à distance grâce à des outils numériques sécurisés." },
];

const testimonials = [
  {
    quote: "Merci à Aurélie et son professionnalisme et son aide pour notre entreprise. Je recommande si vous cherchez quelqu'un d'assidu et de réactif.",
    name: "Real Light Agency",
    role: "Entreprise cliente — avis Google 5/5",
  },
  {
    quote: "Je recommande les services pour le sérieux et l'aide dans la gestion quotidienne de mon entreprise.",
    name: "Marie A.",
    role: "Entreprise cliente — avis Google 5/5",
  },
];

export const Manifesto = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="manifesto-section">
    <Reveal>
      <SectionLabel>Pourquoi ARGIA Admin</SectionLabel>
      <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl lg:text-5xl">
        Un accompagnement de proximité, fondé sur l'expérience et la confiance
      </h2>
    </Reveal>
    <div className="mt-16 divide-y divide-[#2C2A29]/10 border-t border-b border-[#2C2A29]/10">
      {chapters.map((c, i) => (
        <Reveal key={c.n} delay={i * 0.08}>
          <div className="group grid items-start gap-4 py-10 transition-colors duration-300 lg:grid-cols-12 lg:gap-8">
            <span className="font-display text-4xl font-bold text-argia-gold lg:col-span-2">{c.n}</span>
            <h3 className="font-display text-xl font-bold text-argia-ink md:text-2xl lg:col-span-4">{c.title}</h3>
            <p className="text-base leading-relaxed text-argia-muted lg:col-span-6">{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Testimonials = () => (
  <section className="bg-argia-stone py-24" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="text-center">
        <div className="flex justify-center"><SectionLabel>Ils me font confiance</SectionLabel></div>
        <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl lg:text-5xl">
          Ce que disent mes clients
        </h2>
      </Reveal>
      <div className="mx-auto mt-16 grid max-w-4xl gap-8 md:grid-cols-2">
        {testimonials.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.12}>
            <figure className="flex h-full flex-col rounded-2xl border border-[#2C2A29]/10 bg-[#FDFBF7] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <span className="font-display text-5xl leading-none text-argia-gold">“</span>
              <blockquote className="mt-4 flex-1 text-base italic leading-relaxed text-argia-ink">{t.quote}</blockquote>
              <figcaption className="mt-6 border-t border-[#2C2A29]/10 pt-4">
                <p className="text-sm font-semibold text-argia-ink">{t.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-argia-muted">{t.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Zone = () => (
  <section className="relative overflow-hidden" data-testid="zone-section">
    <img src={BASQUE_IMG} alt="Paysage du Pays Basque" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-argia-ink/70" />
    <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10">
      <Reveal>
        <div className="flex items-center gap-2 text-argia-gold">
          <MapPin size={18} strokeWidth={1.5} />
          <span className="text-xs font-semibold uppercase tracking-[0.25em]">Périmètre d'intervention</span>
        </div>
        <h2 className="mt-5 max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Basée à Guiche, proche de chez vous
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80">
          Pays Basque, Sud des Landes et secteurs environnants — en présentiel ou à distance selon vos besoins.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {villes.map((v) => (
            <Link
              key={v.slug}
              to={v.path}
              data-testid={`home-ville-${v.slug}`}
              className="rounded-full border border-white/30 px-4 py-1.5 text-sm text-white/90 backdrop-blur-sm transition-colors duration-300 hover:border-argia-gold hover:bg-white/10 hover:text-white"
            >
              {v.name}
            </Link>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 text-center lg:px-10" data-testid="cta-section">
    <Reveal>
      <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl lg:text-5xl">
        Et si on allégeait votre administratif, dès cette semaine ?
      </h2>
      <p className="mx-auto mt-5 max-w-xl text-base text-argia-muted md:text-lg">
        Répondez à quelques questions rapides et choisissez votre créneau : le premier rendez-vous découverte est offert.
      </p>
      <Link
        to="/rendez-vous"
        data-testid="cta-rdv-button"
        className="mt-10 inline-flex items-center gap-2 rounded-full bg-argia-red px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-0.5 hover:shadow-lg"
      >
        Prendre rendez-vous
        <ArrowRight size={16} strokeWidth={1.5} />
      </Link>
    </Reveal>
  </section>
);
